import { Alert, Box, CircularProgress, DialogContentText } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { CheckMark } from "../../components/UIToolkit/Animations/Checkmark";
import SystemDialog from "../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { SURFACE } from "../../theme/tokens";
import SelfSchedulingCalendar from "./SelfSchedulingCalendar";
import SelfSchedulingMetrics from "./SelfSchedulingMetrics";
import { SelfSchedulingTutorial } from "./SelfSchedulingTutorial";
import { useSelfSchedulingState } from "./state/useSelfSchedulingState";
import { TimePickerHeader } from "./TimePickerHeader";

export const SelfScheduling: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment, refreshNursesData } = useHospitalData();
	const hasFetchedRef = useRef<boolean>(false);
	const hasShownAttentionDialog = useRef<boolean>(false);
	const [isInitializing, setIsInitializing] = useState<boolean>(true);
	const [showSuccessAnimation, setShowSuccessAnimation] = useState<boolean>(false);
	const [showAttentionDialog, setShowAttentionDialog] = useState<boolean>(false);

	const {
		daysData,
		currentMetrics,
		metricsRef,
		isPublishWarningOpen,
		isPublishing,
		handleOnDayClick,
		handleTimeChange,
		togglePublishWarning,
		handlePerformPublish: originalHandlePerformPublish,
		signUpShiftsTargetAmount,
		shiftsPerWeekByEmploymentType,
		selectedDate,
		hasChanges,
		navigateToPreviousPeriod,
		navigateToNextPeriod,
		schedulingPeriod,
		isViewingPastPeriod,
		isViewingLockedPeriod,
		isViewingCurrentPeriod,
		copyLastMonthPreferences,
	} = useSelfSchedulingState();

	const handlePerformPublish = useCallback(async () => {
		await originalHandlePerformPublish();
		setShowSuccessAnimation(true);
		setTimeout(() => setShowSuccessAnimation(false), 2000);
	}, [originalHandlePerformPublish]);

	const init = useCallback(async (): Promise<void> => {
		try {
			setIsInitializing(true);
			await refreshNursesData(chosenDepartment?.department_id);
		} finally {
			setIsInitializing(false);
		}
	}, [chosenDepartment, refreshNursesData]);

	useEffect(() => {
		if (!hasFetchedRef.current) {
			hasFetchedRef.current = true;
			init();
		}
	}, [init]);

	useEffect(() => {
		if (!isInitializing && !hasShownAttentionDialog.current && shiftsPerWeekByEmploymentType) {
			hasShownAttentionDialog.current = true;
			setShowAttentionDialog(true);
		}
	}, [isInitializing, shiftsPerWeekByEmploymentType]);

	const warningMessage = useMemo<string | null>(() => {
		if (!signUpShiftsTargetAmount) return null;

		const shiftsNeeded = signUpShiftsTargetAmount - currentMetrics.signupShiftsCurrentAmount;

		if (shiftsNeeded > 0) {
			return t("self.scheduling.shifts.needed", { count: shiftsNeeded, perWeek: shiftsPerWeekByEmploymentType });
		}

		if (currentMetrics.signupShiftsCurrentAmount > signUpShiftsTargetAmount) {
			const extraShifts = currentMetrics.signupShiftsCurrentAmount - signUpShiftsTargetAmount;
			return t("self.scheduling.extra.shifts", { count: extraShifts });
		}

		return null;
	}, [signUpShiftsTargetAmount, currentMetrics.signupShiftsCurrentAmount, shiftsPerWeekByEmploymentType, t]);

	return (
		<Box
			data-testid="self-scheduling-root"
			sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start" }}
		>
			<ErrorBoundary>
				<Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 2, px: 2, flexWrap: "wrap" }}>
					<SelfSchedulingMetrics
						ref={metricsRef}
						signupShiftsCurrentAmount={currentMetrics.signupShiftsCurrentAmount}
						signupShiftsTargetAmount={signUpShiftsTargetAmount}
					/>
					<TimePickerHeader selectedDate={selectedDate} daysData={daysData} onTimeChange={handleTimeChange} />
				</Box>
			</ErrorBoundary>
			<ErrorBoundary>
				<Box
					sx={{
						width: "100%",
						height: `calc(100% - ${metricsRef.current?.offsetHeight ?? 0}px)`,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "start",
						overflow: "auto",
						pt: 2,
					}}
				>
					<Box
						sx={{
							width: "100%",
							height: "fit-content",
							position: "relative",
						}}
					>
						{(isInitializing || isPublishing) && (
							<Box
								sx={{
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: alpha(SURFACE, 0.8),
									zIndex: 10,
								}}
							>
								<CircularProgress />
							</Box>
						)}
						{showSuccessAnimation && (
							<Box
								sx={{
									position: "absolute",
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									backgroundColor: alpha(SURFACE, 0.95),
									zIndex: 20,
								}}
							>
								<CheckMark />
							</Box>
						)}
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", minHeight: "450px" }}>
							<SelfSchedulingCalendar
								daysData={daysData}
								onDayClick={isViewingPastPeriod ? undefined : handleOnDayClick}
								selectedDate={selectedDate}
								targetWorkingDaysPerWeek={shiftsPerWeekByEmploymentType}
								navigateToPreviousPeriod={navigateToPreviousPeriod}
								navigateToNextPeriod={navigateToNextPeriod}
								schedulingPeriod={schedulingPeriod}
								isViewingPastPeriod={isViewingPastPeriod}
								isViewingLockedPeriod={isViewingLockedPeriod}
								isViewingCurrentPeriod={isViewingCurrentPeriod}
								isInitializing={isInitializing}
								isPublishing={isPublishing}
								copyLastMonthPreferences={copyLastMonthPreferences}
							/>
						</Box>
					</Box>
					<Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "start", gap: 2, pt: 3 }}>
						{!isViewingPastPeriod && (
							<SystemButton
								data-testid="self-scheduling-publish"
								onClick={togglePublishWarning}
								disabled={isViewingLockedPeriod || isInitializing || isPublishing || !hasChanges}
							>
								{t("submit")}
							</SystemButton>
						)}
						<SelfSchedulingTutorial
							open={!chosenDepartment.selfScheduling?.locked && showAttentionDialog}
							onClose={() => setShowAttentionDialog(false)}
							shiftsPerWeekByEmploymentType={shiftsPerWeekByEmploymentType}
						/>
						<SystemDialog
							title={t("publish.preferences")}
							body={
								<Box>
									<DialogContentText>{t("publish.preferences.confirm")}</DialogContentText>
									{warningMessage && (
										<Alert severity={currentMetrics.signupShiftsCurrentAmount < signUpShiftsTargetAmount ? "warning" : "info"} sx={{ mt: 2 }}>
											{warningMessage}
										</Alert>
									)}
								</Box>
							}
							open={isPublishWarningOpen}
							onClose={togglePublishWarning}
							onConfirm={handlePerformPublish}
							confirmDisabled={isPublishing}
						/>
					</Box>
				</Box>
			</ErrorBoundary>
		</Box>
	);
};
